import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PhysicalInfoDimension, PhysicalInfoSource, TrackerCustomEvent, useTracker } from 'common-package';
import { Button } from 'antd-mobile';
import { useLocation } from 'react-router';
import { useMemoizedFn } from 'ahooks';
import service from '@/service';
import { selectCurrentServingOrder } from '@/store/service';
import { Header, HeaderMemberInfo, ScrollView } from '@/components';
import { ALL_PAGES } from '@/constant';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { selectedCurrentCustomer } from '@/store/customer';
import { useNav } from '@/hooks';
import MeasurementContent, { MeasurementContentRefAttrs } from '../body-measurement/measurement-content';

import cx from './index.module.scss';

const BodyMeasurement = () => {
  const nav = useNav();
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const currentCustomer = useSelector(selectedCurrentCustomer);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [isLoadingLastMeasurement, setIsLoadingLastMeasurement] = useState<boolean>(true);
  const [measurementResults, setMeasurementResult] = useState<MeasurementResult>(null);
  const measurementContentRef = useRef<MeasurementContentRefAttrs>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const hasClickedSettle = useRef(false);
  const location = useLocation();
  const { sendEvent } = useTracker();

  const [isScrollTop, setIsScrollTop] = useState<boolean>(true);

  const onScrolling = useMemoizedFn(pos => {
    setIsScrollTop(pos.y > 0);
  });

  const handleScrollElement = () => {
    const maxScrollY = scrollViewRef.current?.bsInstance?.maxScrollY;
    setIsScrollTop(false);
    scrollViewRef.current?.bsInstance?.scrollTo(0, maxScrollY, 200);
  };

  // 确认并结算订单
  const handleConfirm = useCallback(async () => {
    if (hasClickedSettle.current) return;
    hasClickedSettle.current = true;

    try {
      setIsConfirming(true);

      await service.bodyMeasurementApi.saveMeasurementResult({
        serviceOrderId: currentServingOrder.orderId,
        type: 2,
        ...measurementResults,
      });
      if (measurementResults !== null) {
        if (Object?.keys(measurementResults)?.length > 0 && measurementResults.constructor === Object) {
          // 埋点上报 去掉图片的属性
          // eslint-disable-next-line max-depth
          for (const key in measurementResults) {
            // eslint-disable-next-line max-depth
            if (measurementResults.hasOwnProperty(key) && !key?.includes('Image')) {
              sendEvent(TrackerCustomEvent.FillInPhysicalInfoSource, {
                et: 'CLK',
                c1: PhysicalInfoDimension[key],
                c2: PhysicalInfoSource['physiotherapy-record-after'],
              });
            }
          }
        }
      }

      setTimeout(
        () =>
          nav(location.state?.isAllProcessSuccess ? ALL_PAGES.HOME : ALL_PAGES.MEMBER_DETAIL, {
            state: {
              orderId: currentServingOrder.orderId,
              isNeedShowDrawerAfterFinish: true,
              customerId: currentServingOrder?.customerId,
            },
          }),
        200
      );
    } catch (e) {
      console.log(e);
      hasClickedSettle.current = false;
    } finally {
      setIsConfirming(false);
    }
  }, [
    currentServingOrder?.customerId,
    currentServingOrder.orderId,
    location.state?.isAllProcessSuccess,
    measurementResults,
    nav,
    sendEvent,
  ]);

  useEffect(() => {
    const { orderId, productItems } = currentServingOrder || {};

    if (!(orderId && productItems?.includes('RadioFrequency'))) return;
    measurementContentRef.current?.getData(orderId);
    scrollViewRef.current?.handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentServingOrder?.orderId]);

  return (
    <div className={cx('body-remeasurement')}>
      <Header
        title={<HeaderMemberInfo name={currentCustomer?.name} avatarUrl={currentCustomer?.avatarUrl} />}
        backConfig={{ text: '会员详情', action: () => nav('/user/member-detail', { replace: true }) }}
      />
      <ScrollView
        ref={scrollViewRef}
        realTime
        className={cx('content-box', { scrolling: !isScrollTop })}
        onScrolling={onScrolling}
      >
        <div className={cx('content-inner-box')}>
          <MeasurementContent
            ref={measurementContentRef}
            afterMassage
            setLoading={setIsLoadingLastMeasurement}
            currentServingOrder={currentServingOrder}
            setMeasurementResult={setMeasurementResult}
            handleScrollElement={handleScrollElement}
          />
        </div>
      </ScrollView>
      <div className={cx('footer')}>
        <Button
          className={cx('btn', 'btn-primary')}
          loading={isConfirming || isLoadingLastMeasurement}
          onClick={handleConfirm}
        >
          确认并结束
        </Button>
      </div>
    </div>
  );
};

export default BodyMeasurement;
